/** Tiny deterministic math helpers shared across the sim. */

export const TWO_PI = Math.PI * 2;

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Move `from` toward `to` by at most `maxDelta`. */
export function approach(from: number, to: number, maxDelta: number): number {
  if (from < to) return Math.min(from + maxDelta, to);
  if (from > to) return Math.max(from - maxDelta, to);
  return to;
}

/** Shortest signed angular difference a-b, in (-PI, PI]. */
export function angleDelta(a: number, b: number): number {
  let d = (a - b) % TWO_PI;
  if (d < -Math.PI) d += TWO_PI;
  else if (d > Math.PI) d -= TWO_PI;
  return d;
}

/** Normalize an angle into [-PI, PI). */
export function normAngle(a: number): number {
  let x = a % TWO_PI;
  if (x < -Math.PI) x += TWO_PI;
  else if (x >= Math.PI) x -= TWO_PI;
  return x;
}

/** Deterministic seeded PRNG (mulberry32) for reproducible spawns/tests. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
